import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRouteTopBarLook = 'route-bar-back' | 'route-bar-close' | 'route-bar-sun' | 'route-bar-sun-on';

const ROUTE_TOP_BAR_LOOKS = new Set<string>(['route-bar-back', 'route-bar-close', 'route-bar-sun', 'route-bar-sun-on']);
export const isWedgeRouteTopBarLook = (look: string): look is WedgeRouteTopBarLook => ROUTE_TOP_BAR_LOOKS.has(look);

/* Released route plan top bar (Drive, "Plan my day"): Back to territory on the left, the sun mode toggle and Close on the right.
   Back and Close are bare text buttons: no fill and no border, the accent ink, 15px medium type on the `font` shorthand, 6px of padding and a 44px
   minimum height. Back is a flex line with a 4px gap (the glyph and the label); Close is not, so it keeps the default display. The sun toggle is a
   10px radius button with 6px by 8px of padding and a 40px minimum height, centered on a flex line: at rest it has no fill, a 1px card border and
   the accent ink; while sun mode is on it takes the accent fill and a 1px accent border. The ink of the pressed toggle is owned by the caller, which
   reads the on-accent variable with the source fallback. Each reads the exact legacy roles, and the declaration order is the order of the source.
   The caller owns the glyph, the labels and the `aria-pressed` state. */
const BAR_FONT = '500 15px', BAR_PADDING = 6, BAR_HEIGHT = 44, BACK_GAP = 4;
const SUN_PADDING = '6px 8px', SUN_RADIUS = 10, SUN_HEIGHT = 40;

export function wedgeRouteTopBarStyle(look: WedgeRouteTopBarLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'route-bar-back') return {
    flex: '0 0 auto', background: 'none', border: 0, color: r('accent'), font: `${BAR_FONT} ${r('font')}`, cursor: 'pointer', padding: BAR_PADDING,
    minHeight: BAR_HEIGHT, display: 'flex', alignItems: 'center', gap: BACK_GAP,
  };
  if (look === 'route-bar-close') return {
    flex: '0 0 auto', background: 'none', border: 0, color: r('accent'), font: `${BAR_FONT} ${r('font')}`, cursor: 'pointer', padding: BAR_PADDING,
    minHeight: BAR_HEIGHT,
  };
  if (look === 'route-bar-sun') return {
    flex: '0 0 auto', background: 'none', border: `1px solid ${r('cardBorder')}`, borderRadius: SUN_RADIUS, color: r('accent'), cursor: 'pointer',
    padding: SUN_PADDING, minHeight: SUN_HEIGHT, display: 'flex', alignItems: 'center',
  };
  return {
    flex: '0 0 auto', background: r('accent'), border: `1px solid ${r('accent')}`, borderRadius: SUN_RADIUS, cursor: 'pointer',
    padding: SUN_PADDING, minHeight: SUN_HEIGHT, display: 'flex', alignItems: 'center',
  };
}

/* No look draws the transparent target. Back and Close are 44px tall already and the three controls sit 8px apart on one line, where a region
   would grow into the next control; the sun toggle is 40px tall and its region would reach both neighbors. */
export const wedgeRouteTopBarDrawsHit = (look: string): boolean => !isWedgeRouteTopBarLook(look);
